/**
 * A Document Form (obj_type `E`) placed directly on the application menu.
 *
 * The usual way into a document is the fourth VIEW of its anchor transaction —
 * a person opens the invoice list and switches to Document. This is the other
 * way: a menu entry whose whole purpose is "fill in one of these". Both mount
 * the same `<DocumentFormView>` from `@twasta/page-runtime/er`, so the record
 * is identical whichever door somebody came through.
 */

import { Suspense, lazy, useCallback, useMemo } from 'react'
import { documentFormRuntime } from '@/api/er'
import { makeDmsClient } from '@/api/dms'

// Lazy for the reason `useDocumentInterface` states: the document view carries
// the whole TipTap editor stack, and nothing that is not a document should pay
// for it. Same pattern the DMS uses for the same editor.
const DocumentFormView = lazy(async () => ({
  default: (await import('@twasta/page-runtime/er')).DocumentFormView,
}))

export default function DocumentFormPane({ project, objName, onOpenFiled }: {
  project: string
  objName: string
  /**
   * Open the filed copy in its repository. Supplied by the Run shell, which is
   * the only thing here that can move between objects; the builder's preview
   * leaves it out and the document simply does not offer the link.
   */
  onOpenFiled?: (args: { repositoryObj: string; documentId: string }) => void
}) {
  const runtime = useMemo(
    () => documentFormRuntime(project, objName), [project, objName])

  /** So the parties can sign the filed copy here. See `useDocumentInterface`. */
  const dmsClientFor = useCallback(
    (repositoryObj: string) => (repositoryObj
      ? makeDmsClient(project, repositoryObj) : null),
    [project])

  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex',
      flexDirection: 'column' }}>
      <Suspense fallback={<div style={{
        padding: 24, fontSize: '0.9125rem', color: 'var(--color-text-secondary)',
      }}>Opening the document…</div>}>
      <DocumentFormView
        project={project}
        // Reached from the menu, so there is no anchor transaction screen
        // around it; the form's own anchor is what it records against and the
        // server resolves that from the model.
        objName={objName}
        formObj={objName}
        runtime={runtime}
        onOpenFiled={onOpenFiled}
        dmsClientFor={dmsClientFor}
      />
      </Suspense>
    </div>
  )
}
